<script setup lang="ts">
import { useTranslation, WCEvents, SENIOR_GET_QUOTE_URL, MEDICARE_URL } from '@hfc/ui-core';
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { usePageStore } from '@/page/infrastructure/composition/usePageStore';
import { isBlocked } from '@/page/domain/ChatApp';

const { emitVanillaJSCompatible } = WCEvents();
const { t } = useTranslation();
const { pageState } = usePageStore();

const container = ref();
const now = ref(Date.now());

const today = new Date();
const season = today.getMonth() < 9 ? today.getFullYear() - 1 : today.getFullYear();

const DEFAULT_PRESET = {
  phases: [
    { start: new Date(`${season}-12-25T00:00:00-08:00`).getTime(), end: new Date(`${season + 1}-01-01T00:00:00-08:00`).getTime(), message: 'header.oepDeadline', countdown: true },
    { start: new Date(`${season + 1}-01-25T00:00:00-08:00`).getTime(), end: new Date(`${season + 1}-02-01T00:00:00-08:00`).getTime(), message: 'header.oepEnds', countdown: true },
  ],
};
const PRESETS = [
  {
    uris: [MEDICARE_URL, SENIOR_GET_QUOTE_URL],
    phases: [{ start: new Date(`${season}-10-01T00:00:00-07:00`).getTime(), end: new Date(`${season}-12-08T00:00:00-08:00`).getTime(), message: 'header.medicareCountdown', countdown: true }],
  },
];
const { phases } = PRESETS.find(({ uris }) => isBlocked(uris.map(uri => `${uri}*`))) ?? DEFAULT_PRESET;

const phase = computed(() => phases.find(({ end }) => now.value < end));
const secondsLeft = computed(() => Math.floor(Math.max(0, (phase.value?.end ?? 0) - now.value) / 1000));
const countdownUnits = computed(() => [
  { value: Math.floor(secondsLeft.value / 86400), label: 'header.days' },
  { value: Math.floor((secondsLeft.value % 86400) / 3600), label: 'header.hrs' },
  { value: Math.floor((secondsLeft.value % 3600) / 60), label: 'header.mins' },
  { value: secondsLeft.value % 60, label: 'header.secs' },
]);
const showBanner = computed(() => {
  const bannerHeader = pageState.value.globalSettings?.bannerHeader;
  if (!bannerHeader?.enabled || !phase.value || now.value < phase.value.start) {
    return false;
  }

  return !isBlocked([...bannerHeader.getQuoteRoutesDisabled]);
});

const emitHasBanner = (value: boolean): void => {
  emitVanillaJSCompatible('has-banner-change', container.value, { value });
};

watch(showBanner, emitHasBanner);

let interval: ReturnType<typeof setInterval> | undefined;
onMounted(() => {
  emitHasBanner(showBanner.value);
  interval = setInterval(() => {
    now.value = Date.now();
    if (!phase.value) {
      clearInterval(interval);
    }
  }, 1000);
});

onBeforeUnmount(() => clearInterval(interval));
</script>
<template>
  <div ref="container">
    <div v-if="showBanner && phase" class="bg-red-100 text-red font-bold text-sm md:text-2xl py-1.5 px-2 flex flex-wrap items-center justify-center gap-x-5 gap-y-1 text-center">
      <span class="flex items-center gap-x-3">
        <hfc-icon icon="calendar-days" class="w-6 md:w-8 flex" />
        {{ t(phase.message) }}
      </span>
      <span v-if="phase.countdown" class="flex gap-x-1.5">
        <span v-for="unit in countdownUnits" :key="unit.label" class="bg-red-200 rounded-md w-10 md:w-12 py-0.5 flex flex-col items-center text-gray-900 leading-tight">
          <span class="text-base md:text-xl lining-nums">{{ unit.value }}</span>
          <span class="text-[0.5rem] md:text-[0.625rem] uppercase">{{ t(unit.label) }}</span>
        </span>
      </span>
    </div>
  </div>
</template>
